/**
 * Mirror a tactics layout across the pitch's centre line.
 *
 * The coach authors one side of a play (say, a press starting from the
 * left half-space) and one-click produces the mirror (the same press
 * starting from the right half-space). Equivalent to TacticalPad's
 * "Reflect" tool.
 *
 * Two axis options:
 *   - `'x'` — sideline ↔ sideline. Negates every world `x`. Use for
 *     left↔right mirrors.
 *   - `'z'` — goal ↔ goal. Negates every world `z`. Use for
 *     attacking↔defending mirrors. Note this also swaps the home/away
 *     half mental model since `+z` is the home goal and `-z` is away.
 *
 * Fields mirrored:
 *   - homeSpots / awaySpots (x, z)
 *   - arrows[].points[] (x, z)
 *   - keyframes[] (recurses into per-keyframe spots + arrows)
 *   - branches[].keyframes[] (recurses similarly)
 *   - zones[] — geom interpretation depends on shape; we handle rect /
 *     circle / polygon
 *   - coachingPoints[].at (x, z)
 *   - drawings[] — best-effort: if the drawing carries a `points` array
 *     of `{x, z}` pairs, mirror each. Unknown drawing fields pass
 *     through unchanged.
 *
 * Fields left unchanged (intentionally):
 *   - playerRoles — role tags don't have coordinates
 *   - teamTactics — slider values, no coordinates
 *   - customFormations — template positions (a library of options, not
 *     an instance to flip)
 *   - setPieces — permissively typed; positions live inside opaque
 *     bags. Mirror'd as part of a follow-up once the set-piece shape
 *     gets concrete coordinate fields.
 *   - overlays — kind/opacity state, no coordinates
 *   - voiceovers — audio/transcript, no coordinates
 *   - constraints — text only
 *   - v + saved_at — schema + timestamp metadata
 */

import type {
  TacticsArrow,
  TacticsBranch,
  TacticsCoachingPoint,
  TacticsDrawing,
  TacticsKeyframe,
  TacticsLayout,
  TacticsPlayerSpot,
  TacticsZone,
} from './types';

/** Which axis to reflect across. */
export type MirrorAxis = 'x' | 'z';

/** Negate a coordinate, normalising the JS `-0` quirk to `0` so test
 *  equality checks behave predictably and serialised JSON is clean. */
function neg(v: number): number {
  return v === 0 ? 0 : -v;
}

function mirrorSpot(spot: TacticsPlayerSpot, axis: MirrorAxis): TacticsPlayerSpot {
  return axis === 'x' ? { ...spot, x: neg(spot.x) } : { ...spot, z: neg(spot.z) };
}

function mirrorPoint<P extends { x: number; z: number }>(
  pt: P,
  axis: MirrorAxis,
): P {
  return axis === 'x' ? { ...pt, x: neg(pt.x) } : { ...pt, z: neg(pt.z) };
}

function mirrorArrow(arrow: TacticsArrow, axis: MirrorAxis): TacticsArrow {
  return {
    ...arrow,
    points: arrow.points.map((p) => mirrorPoint(p, axis)),
  };
}

function mirrorZone(zone: TacticsZone, axis: MirrorAxis): TacticsZone {
  const g = zone.geom;
  if (zone.shape === 'rect') {
    // geom = [cx, cz, width, depth]
    if (g.length < 4) return zone;
    const [cx, cz, w, d] = g;
    return {
      ...zone,
      geom: [axis === 'x' ? neg(cx!) : cx!, axis === 'z' ? neg(cz!) : cz!, w!, d!],
    };
  }
  if (zone.shape === 'circle') {
    // geom = [cx, cz, radius]
    if (g.length < 3) return zone;
    const [cx, cz, r] = g;
    return {
      ...zone,
      geom: [axis === 'x' ? neg(cx!) : cx!, axis === 'z' ? neg(cz!) : cz!, r!],
    };
  }
  // polygon — flat [x, z, x, z, ...]
  const flipped: number[] = [];
  for (let i = 0; i < g.length; i += 2) {
    const x = g[i];
    const z = g[i + 1];
    if (typeof x !== 'number' || typeof z !== 'number') continue;
    flipped.push(axis === 'x' ? neg(x) : x);
    flipped.push(axis === 'z' ? neg(z) : z);
  }
  return { ...zone, geom: flipped };
}

function mirrorCoachingPoint(
  pt: TacticsCoachingPoint,
  axis: MirrorAxis,
): TacticsCoachingPoint {
  return { ...pt, at: mirrorPoint(pt.at, axis) };
}

function mirrorKeyframe(kf: TacticsKeyframe, axis: MirrorAxis): TacticsKeyframe {
  return {
    ...kf,
    homeSpots: kf.homeSpots.map((s) => mirrorSpot(s, axis)),
    awaySpots: kf.awaySpots.map((s) => mirrorSpot(s, axis)),
    arrows: kf.arrows.map((a) => mirrorArrow(a, axis)),
  };
}

function mirrorBranch(b: TacticsBranch, axis: MirrorAxis): TacticsBranch {
  return { ...b, keyframes: b.keyframes.map((k) => mirrorKeyframe(k, axis)) };
}

/** Drawings are typed permissively (`Record<string, unknown>`), but at
 *  runtime most carry a `points: { x, z }[]` array. Mirror those when
 *  present; pass everything else through unchanged. */
function mirrorDrawing(d: TacticsDrawing, axis: MirrorAxis): TacticsDrawing {
  const points = (d as { points?: unknown }).points;
  if (!Array.isArray(points)) return d;
  const next = points.map((p) => {
    if (
      p &&
      typeof p === 'object' &&
      typeof (p as { x: unknown }).x === 'number' &&
      typeof (p as { z: unknown }).z === 'number'
    ) {
      const pt = p as { x: number; z: number; [k: string]: unknown };
      return axis === 'x' ? { ...pt, x: neg(pt.x) } : { ...pt, z: neg(pt.z) };
    }
    return p;
  });
  return { ...d, points: next } as TacticsDrawing;
}

/**
 * Return a new `TacticsLayout` with every coordinate-bearing field
 * reflected across the centre line on the given axis. Pure — does not
 * mutate the input.
 */
export function mirrorTacticsLayout(
  layout: TacticsLayout,
  axis: MirrorAxis,
): TacticsLayout {
  return {
    ...layout,
    homeSpots: layout.homeSpots.map((s) => mirrorSpot(s, axis)),
    awaySpots: layout.awaySpots.map((s) => mirrorSpot(s, axis)),
    arrows: layout.arrows.map((a) => mirrorArrow(a, axis)),
    keyframes: layout.keyframes?.map((k) => mirrorKeyframe(k, axis)),
    branches: layout.branches?.map((b) => mirrorBranch(b, axis)),
    zones: layout.zones?.map((z) => mirrorZone(z, axis)),
    coachingPoints: layout.coachingPoints?.map((p) =>
      mirrorCoachingPoint(p, axis),
    ),
    drawings: layout.drawings?.map((d) => mirrorDrawing(d, axis)),
  };
}
